import { UpdateIcon } from '@radix-ui/react-icons';
import { Badge, Box, Flex, Heading, Link, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import { CommonTableButton } from 'components/common/table/button';
import { CommonTooltip } from 'components/common/tooltip';
import { RequestChangeButton } from 'components/sections/admin-portal/error-types/request-change-button';
import {
  formatDate,
  translateUserErrorMessage,
} from 'components/sections/admin-portal/machines/dialogs/settings/machine-errors/common';
import { ErrorDetails } from 'components/sections/admin-portal/machines/dialogs/settings/machine-errors/machine-error-details';
import { MachineErrorsToolbar } from 'components/sections/admin-portal/machines/dialogs/settings/machine-errors/machine-errors-toolbar';
import { useMachineErrorsDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineErrorsDialogState';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { MACHINE_SETTINGS_CONTENT_ID } from 'enums/admin-portal';
import usePrevious from 'hooks/usePrevious';
import { t } from 'i18next';
import { ErrorTableStaticValues } from 'interfaces/i-machine-errors-table';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITableInfiniteLoader } from 'interfaces/tables/infinite-loader';
import { ITableSelectable } from 'interfaces/tables/selection';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { DEFAULT_ERROR_LEVEL, ErrorLevel } from 'lib_ts/enums/errors.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IError } from 'lib_ts/interfaces/common/i-error';
import { IErrorType } from 'lib_ts/interfaces/common/i-error-type';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AdminErrorTypesService } from 'services/admin/error-types.service';
import { AdminMachinesService } from 'services/admin/machines.service';
import { MainService } from 'services/main.service';
import { useAuthStore } from 'stores/use-auth-store';

const RECORDS_PER_REQUEST = 25;

export const MachineErrors = () => {
  const current = useAuthStore((s) => s.current);
  const machine = useMachinesPortalStore((s) => s.machine);

  const { errorID, errorFilters, resetErrorID, setErrorID, viewSenderErrors } =
    useMachineErrorsDialogState();

  const initializedRef = useRef(false);
  const prevFilters = usePrevious(errorFilters);

  const [loading, setLoading] = useState(false);
  const [loadingErrorDetails, setLoadingErrorDetails] = useState(false);

  // total number of errors matching the errorFilters, as provided by the server
  const [total, setTotal] = useState(0);
  const [data, setData] = useState<IError[]>([]);

  // show ErrorDetails when an error is selected
  const [selectedError, setSelectedError] = useState<IError>();
  const [types, setTypes] = useState<IErrorType[]>([]);

  // used to persist table scroll position when navigating to/from error details
  const scrollPositionRef = useRef<number>();

  const getInternal = useCallback(
    (error: IError) => {
      const type = types.find((t) => t.errorID === error.errorID);
      if (!type) {
        return ErrorTableStaticValues.NA;
      }

      return type.internal
        ? ErrorTableStaticValues.True
        : ErrorTableStaticValues.False;
    },
    [types]
  );

  const getSlack = useCallback(
    (error: IError) => {
      const type = types.find((t) => t.errorID === error.errorID);
      if (!type) {
        return ErrorTableStaticValues.NA;
      }

      return type.slack
        ? ErrorTableStaticValues.True
        : ErrorTableStaticValues.False;
    },
    [types]
  );

  const getOwner = useCallback(
    (error: IError) => {
      const type = types.find((t) => t.errorID === error.errorID);
      if (!type) {
        return ErrorTableStaticValues.NA;
      }

      if (!type.department) {
        return ErrorTableStaticValues.NA;
      }

      return type.department;
    },
    [types]
  );

  const columns = useMemo<ITypedTableColumn<IError>[]>(
    () => [
      {
        label: 'Created',
        key: '_created',
        formatFn: (m) => <Text size="1">{formatDate(m._created)}</Text>,
      },
      {
        label: 'ID',
        key: 'errorID',
        formatFn: (m) =>
          m.errorID ? (
            <Badge style={{ maxWidth: '100%' }} color={RADIX.COLOR.NEUTRAL}>
              <Text truncate>{m.errorID}</Text>
            </Badge>
          ) : undefined,
      },
      {
        label: 'Level',
        key: 'levelOverride',
        formatFn: (m) => {
          const level = m.levelOverride ?? DEFAULT_ERROR_LEVEL;
          return (
            <Badge color={NotifyHelper.getColorFromLevel(level)}>
              {level.toUpperCase() ?? ErrorTableStaticValues.NA}
            </Badge>
          );
        },
      },
      {
        label: 'Internal',
        key: 'internal',
        formatFn: (m) => {
          const internal = getInternal(m);
          return (
            <Badge
              color={
                internal === ErrorTableStaticValues.True
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.DANGER
              }
            >
              {internal}
            </Badge>
          );
        },
      },
      {
        label: 'In Slack',
        key: 'in-slack',
        formatFn: (m) => {
          const slack = getSlack(m);
          return (
            <Badge
              color={
                slack === ErrorTableStaticValues.True
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.DANGER
              }
            >
              {slack}
            </Badge>
          );
        },
      },
      {
        label: 'Owner',
        key: 'department',
        formatFn: (m) => {
          const owner = getOwner(m);
          return <Text>{owner}</Text>;
        },
      },
      {
        label: 'Sender',
        key: 'sender',
        formatFn: (m) => {
          if (!m.sender) {
            return null;
          }

          return (
            <Link
              onClick={(e) => {
                if (!m.sender) {
                  return;
                }

                // avoid triggering select row functionality
                e.stopPropagation();

                // update filter to this specific sender
                viewSenderErrors(m.sender);
              }}
              className="cursor-pointer font-mono"
              truncate
            >
              {m.sender.replace(' ', ' > ')}
            </Link>
          );
        },
      },
      {
        label: 'Message',
        key: '_message',
        disableSort: true,
        formatFn: (m) => {
          const typeDef = types.find((t) => t.errorID === m.errorID);
          const message = typeDef
            ? translateUserErrorMessage(typeDef, current.language)
            : (m as any).message;

          if (!message) {
            return null;
          }

          return (
            <CommonTooltip
              trigger={
                <Box className="clamp-message" py="1">
                  <Text>{message}</Text>
                </Box>
              }
              text={message}
            />
          );
        },
      },
    ],
    [types, getInternal, getSlack]
  );

  /**
   * Filtering requires some massaging of types as some errors may not have an ErrorID / associated ErrorType.
   * Setting a filter to ErrorTableStaticValues.NA will specifically filter for errors missing that field (see getLevel, getInternal, getSlack).
   *
   * Inputs use IErrorTableFilters as a working type, but we'll coerce values to match IErrorsQuery before sending to the server.
   */
  const loadData = useCallback(
    async (skip: number, silently?: boolean) => {
      if (!machine?.machineID) {
        return;
      }

      try {
        if (!silently) {
          setLoading(true);
        }

        const coercedDateRange = ():
          | {
              start_date?: string;
              end_date?: string;
            }
          | undefined => {
          if (!errorFilters.dateRange) {
            return undefined;
          }

          return {
            start_date: errorFilters.dateRange.start.toISOString(),
            end_date: errorFilters.dateRange.end.toISOString(),
          };
        };

        const coercedLevel = (): (ErrorLevel | null)[] | undefined => {
          if (errorFilters.level.length === 0) {
            return undefined;
          }

          // ErrorTableStaticValues.NA -> null
          return errorFilters.level.map((level) =>
            level === ErrorTableStaticValues.NA ? null : level
          );
        };

        const coercedInternal = (): boolean | null | undefined => {
          if (errorFilters.internal === '') {
            return undefined;
          }

          // ErrorTableStaticValues.NA -> null
          if (errorFilters.internal === ErrorTableStaticValues.NA) {
            return null;
          }

          // 'True' -> true, 'False' -> false
          return errorFilters.internal === ErrorTableStaticValues.True;
        };

        const coercedSlack = (): boolean | null | undefined => {
          if (errorFilters.slack === '') {
            return undefined;
          }

          // ErrorTableStaticValues.NA -> null
          if (errorFilters.slack === ErrorTableStaticValues.NA) {
            return null;
          }

          // 'True' -> true, 'False' -> false
          return errorFilters.slack === ErrorTableStaticValues.True;
        };

        const coercedSender = (): string | undefined => {
          const sanitizedSender = errorFilters.sender.trim();
          return sanitizedSender.length === 0 ? undefined : sanitizedSender;
        };

        const coercedErrorTypeID = (): string | undefined => {
          const sanitizedErrorTypeID = errorFilters.errorTypeID.trim();
          return sanitizedErrorTypeID.length === 0
            ? undefined
            : sanitizedErrorTypeID;
        };

        const { total: dbTotal, data: latest } =
          await AdminMachinesService.getInstance().getErrors({
            machineID: machine.machineID,
            level: coercedLevel(),
            internal: coercedInternal(),
            slack: coercedSlack(),
            sender: coercedSender(),
            errorTypeID: coercedErrorTypeID(),
            ...coercedDateRange(), // start_date, end_date
            limit: RECORDS_PER_REQUEST,
            skip,
          });

        setTotal(dbTotal);

        if (skip > 0) {
          setData((prev) => [...prev, ...latest]);
          return;
        }

        setData(latest);
      } catch (e) {
        NotifyHelper.error({
          message_md: `Unable to load errors: ${e}`,
        });
      } finally {
        // react update batching causing some annoying flickering
        setTimeout(() => {
          setLoading(false);
        }, 0);
      }
    },
    [
      // deeper comparison of errorFilters for efficiency
      errorFilters.dateRange,
      errorFilters.level,
      errorFilters.internal,
      errorFilters.slack,
      errorFilters.sender,
      errorFilters.errorTypeID,
    ]
  );

  useEffect(() => {
    if (!machine?.machineID || initializedRef.current) {
      return;
    }

    const fetchErrorTypes = async () => {
      try {
        const result = await AdminErrorTypesService.getInstance().getAll();

        if (!result) {
          console.warn('Failed to load error types for machine errors');
          return;
        }

        setTypes(result);
      } catch (e) {
        console.error(e);
      }
    };

    const init = async () => {
      setLoading(true);
      await fetchErrorTypes();
      await loadData(0, false);
      setLoading(false);
    };

    init();
    initializedRef.current = true;
  }, [machine?.machineID, loadData]);

  useEffect(() => {
    if (!initializedRef.current) {
      return;
    }

    // queries can be expensive, so we'll do a manual equality check here just in case
    // todo: is this actually necessary?
    if (!prevFilters || isEqual(prevFilters, errorFilters)) {
      return;
    }

    loadData(0);
  }, [errorFilters, loadData]);

  const onSelect = (m?: IError) => {
    if (!m) {
      return;
    }

    // save our scroll position so we can restore it later when the user goes back to the table
    const contentEl = document.getElementById(MACHINE_SETTINGS_CONTENT_ID);
    scrollPositionRef.current = contentEl?.scrollTop;

    setSelectedError(m);
  };

  const onBack = () => {
    setSelectedError(undefined);

    // remove errorID from URL params
    resetErrorID();
  };

  // we do not fetch error exceptions when we load the table view - only when we lazy load the entire error after selection
  const selectedErrorFullyLoaded = !!(
    errorID &&
    selectedError?._id === errorID &&
    selectedError?.exception
  );

  // handles loading the full error document after selection or when linking directly to an error via URL params
  useEffect(() => {
    if (selectedErrorFullyLoaded) {
      return;
    }

    const lazyLoadError = async (id: string) => {
      setLoadingErrorDetails(true);
      const result = await MainService.getInstance().getError({
        errorID: id,
      });

      if (!result) {
        NotifyHelper.warning({
          message_md: `No error found with ID ${id}`,
        });

        resetErrorID();
        setLoadingErrorDetails(false);
        return;
      }

      setSelectedError(result);
      setLoadingErrorDetails(false);
    };

    // linked directly to this error without going through the table first
    if (errorID && !selectedError?._id) {
      lazyLoadError(errorID);
      return;
    }

    console.debug('** what do we have', {
      errorID,
      selected: selectedError?._id,
    });

    // nothing selected
    if (!selectedError?._id) {
      return;
    }

    // table row selected, load full error details
    setErrorID(selectedError._id);
    lazyLoadError(selectedError._id);
  }, [selectedError?._id, errorID, selectedErrorFullyLoaded]);

  useEffect(() => {
    // todo: is this more efficient to do with a ref?
    const contentEl = document.getElementById(MACHINE_SETTINGS_CONTENT_ID);

    if (!selectedError) {
      // restore our previous scroll position
      contentEl?.scrollTo({
        top: scrollPositionRef.current,
      });
      return;
    }

    contentEl?.scrollTo({
      top: 0,
    });
  }, [selectedError]);

  const tableSelection: ITableSelectable = {
    enableSelect: true,
    afterChangeSelected: onSelect,
  };

  const tableInfiniteScrollable: ITableInfiniteLoader = {
    onLoadMore: () => loadData(data.length, true),
    hasMore: data.length < total,
    rootMargin: '128px',
  };

  return (
    <ErrorBoundary identifier="MachineDetailsErrorsTab">
      <Flex className={RADIX.VFLEX.GROW} direction="column">
        {selectedError && (
          <Box>
            <ErrorDetails
              error={selectedError}
              errorType={types.find((t) => t.errorID === selectedError.errorID)}
              onBack={onBack}
              loading={loadingErrorDetails}
            />
          </Box>
        )}
        <Flex
          className={StringHelper.classNames([
            RADIX.VFLEX.GROW,
            /* always keep table in the DOM to persist input states */
            selectedError ? 'hidden' : '',
          ])}
          direction="column"
          gap={RADIX.FLEX.GAP.SM}
        >
          <Flex gap={RADIX.FLEX.GAP.SM} align="center">
            <Box flexGrow="1">
              <Heading size="4">{t('common.errors')}</Heading>
            </Box>

            <Box>
              <RequestChangeButton />
            </Box>

            <CommonTableButton
              icon={<UpdateIcon />}
              label="common.refresh"
              variant={RADIX.BUTTON.VARIANT.TABLE}
              onClick={() => loadData(0, false)}
              loading={loading}
            />
          </Flex>
          <Flex
            direction="column"
            gap={RADIX.FLEX.GAP.MD}
            className={RADIX.VFLEX.GROW}
          >
            <FlexTableWrapper
              gap={RADIX.FLEX.GAP.FORM}
              header={<MachineErrorsToolbar />}
              table={
                <CommonTableHoC
                  dataTestID="MachineErrorsLog"
                  identifier="MachineErrorsLogTable"
                  displayColumns={columns}
                  displayData={data}
                  loading={loading}
                  {...tableSelection}
                  {...tableInfiniteScrollable}
                  vFlex
                  skipResetSelected
                />
              }
            />
          </Flex>
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};
